import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import StorageIcon from '@mui/icons-material/Storage';
import { Box, Typography, TextField, Chip } from '@mui/material';
import { DbSchema, DbColumn } from '@/services/dbSchema';

type Position = { x: number; y: number };

// Color coding by table prefix
const TABLE_COLORS: Record<string, { bg: string; border: string; header: string }> = {
  seo_: { bg: '#EFF6FF', border: '#3B82F6', header: '#1D4ED8' },
  fx_: { bg: '#FEF3C7', border: '#F59E0B', header: '#D97706' },
  ceox_: { bg: '#F0FDF4', border: '#10B981', header: '#059669' },
  pipedrive_: { bg: '#FAF5FF', border: '#8B5CF6', header: '#7C3AED' },
  google_: { bg: '#ECFDF5', border: '#34D399', header: '#059669' },
  smartlead_: { bg: '#EEF2FF', border: '#6366F1', header: '#4F46E5' },
};

function getTableColor(name: string): { bg: string; border: string; header: string } {
  for (const [prefix, colors] of Object.entries(TABLE_COLORS)) {
    if (name.startsWith(prefix)) return colors;
  }
  return { bg: '#F9FAFB', border: '#D1D5DB', header: '#374151' };
}

function getTypeLabel(col: DbColumn): string {
  if (col.udtName === 'uuid') return 'uuid';
  if (col.udtName === 'text') return 'text';
  if (col.udtName === 'int4') return 'int';
  if (col.udtName === 'int8') return 'bigint';
  if (col.udtName === 'bool') return 'bool';
  if (col.udtName === 'timestamptz') return 'timestamp';
  if (col.udtName === 'timestamp') return 'timestamp';
  if (col.udtName === 'date') return 'date';
  if (col.udtName === 'numeric') return 'numeric';
  if (col.udtName === 'jsonb') return 'jsonb';
  if (col.udtName === 'json') return 'json';
  if (col.udtName === '_text') return 'text[]';
  if (col.udtName === '_uuid') return 'uuid[]';
  if (col.udtName.startsWith('_')) return `${col.udtName.slice(1)}[]`;
  return col.udtName;
}

// Simple grid layout
function computeLayout(tableNames: string[]): Map<string, Position> {
  const positions = new Map<string, Position>();
  const cols = Math.ceil(Math.sqrt(tableNames.length));
  const colWidth = 300;
  const rowHeight = 400;

  // Group by prefix for visual clustering
  const groups: Record<string, string[]> = {};
  for (const name of tableNames) {
    const prefix = Object.keys(TABLE_COLORS).find((p) => name.startsWith(p)) ?? 'other';
    if (!groups[prefix]) groups[prefix] = [];
    groups[prefix].push(name);
  }

  let idx = 0;
  for (const group of Object.values(groups)) {
    group.sort();
    for (const name of group) {
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      positions.set(name, { x: 40 + col * colWidth, y: 40 + row * rowHeight });
      idx += 1;
    }
  }

  return positions;
}

type Props = {
  schema: DbSchema;
};

const DbErdComponent = function ({ schema }: Props): JSX.Element {
  const { tables, columns, foreignKeys } = schema;
  const tableNames = useMemo(() => tables.map((t) => t.name).sort(), [tables]);

  const [positions, setPositions] = useState<Map<string, Position>>(() =>
    computeLayout(tableNames)
  );
  const [zoom, setZoom] = useState(0.6);
  const [pan, setPan] = useState<Position>({ x: 0, y: 0 });
  const [search, setSearch] = useState('');
  const [selectedTable, setSelectedTable] = useState<string | null>(null);

  const dragRef = useRef<{
    table: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);
  const panRef = useRef<{
    startX: number;
    startY: number;
    origPanX: number;
    origPanY: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Columns grouped by table
  const columnsByTable = useMemo(() => {
    const map = new Map<string, DbColumn[]>();
    for (const col of columns) {
      const existing = map.get(col.table) ?? [];
      existing.push(col);
      map.set(col.table, existing);
    }
    for (const [, cols] of map) {
      cols.sort((a, b) => a.ordinal - b.ordinal);
    }
    return map;
  }, [columns]);

  // FK lookup: which columns are FKs
  const fkColumns = useMemo(() => {
    const set = new Set<string>();
    for (const fk of foreignKeys) {
      set.add(`${fk.sourceTable}.${fk.sourceColumn}`);
    }
    return set;
  }, [foreignKeys]);

  // PK detection (columns named 'id' or ending in '_id' that are the first column)
  const pkColumns = useMemo(() => {
    const set = new Set<string>();
    for (const [table, cols] of columnsByTable) {
      const idCol = cols.find((c) => c.name === 'id');
      if (idCol) set.add(`${table}.id`);
    }
    return set;
  }, [columnsByTable]);

  // Connected tables for highlighting
  const connectedTables = useMemo(() => {
    if (!selectedTable) return new Set<string>();
    const set = new Set<string>();
    set.add(selectedTable);
    for (const fk of foreignKeys) {
      if (fk.sourceTable === selectedTable) set.add(fk.targetTable);
      if (fk.targetTable === selectedTable) set.add(fk.sourceTable);
    }
    return set;
  }, [selectedTable, foreignKeys]);

  // Filter tables by search
  const visibleTables = useMemo(() => {
    if (!search.trim()) return tableNames;
    const q = search.toLowerCase();
    return tableNames.filter((t) => t.toLowerCase().includes(q));
  }, [tableNames, search]);

  // Recompute layout when filter changes
  useEffect(() => {
    setPositions((prev) => {
      const next = new Map(prev);
      for (const name of visibleTables) {
        if (!next.has(name)) {
          next.set(name, { x: 40 + Math.random() * 800, y: 40 + Math.random() * 600 });
        }
      }
      return next;
    });
  }, [visibleTables]);

  // Drag handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent, table: string) => {
      e.stopPropagation();
      const pos = positions.get(table);
      if (!pos) return;
      dragRef.current = { table, startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
    },
    [positions]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (dragRef.current) {
        const { table, startX, startY, origX, origY } = dragRef.current;
        const dx = (e.clientX - startX) / zoom;
        const dy = (e.clientY - startY) / zoom;
        setPositions((prev) => {
          const next = new Map(prev);
          next.set(table, { x: origX + dx, y: origY + dy });
          return next;
        });
        return;
      }
      if (panRef.current) {
        const { startX, startY, origPanX, origPanY } = panRef.current;
        setPan({ x: origPanX + (e.clientX - startX), y: origPanY + (e.clientY - startY) });
      }
    },
    [zoom]
  );

  const handleMouseUp = useCallback(() => {
    dragRef.current = null;
    panRef.current = null;
  }, []);

  const handleBgMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === containerRef.current || (e.target as HTMLElement).tagName === 'svg') {
        panRef.current = { startX: e.clientX, startY: e.clientY, origPanX: pan.x, origPanY: pan.y };
        setSelectedTable(null);
      }
    },
    [pan]
  );

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((z) => Math.max(0.15, Math.min(2, z - e.deltaY * 0.001)));
  }, []);

  // Compute FK lines between visible tables
  const visibleFKs = useMemo(() => {
    const visSet = new Set(visibleTables);
    return foreignKeys.filter((fk) => visSet.has(fk.sourceTable) && visSet.has(fk.targetTable));
  }, [foreignKeys, visibleTables]);

  // Get the Y offset for a column in a table
  const getColumnY = (table: string, column: string): number => {
    const cols = columnsByTable.get(table) ?? [];
    const idx = cols.findIndex((c) => c.name === column);
    return 32 + (idx >= 0 ? idx : 0) * 22 + 11;
  };

  // Unique prefixes for legend
  const prefixes = useMemo(() => {
    const set = new Set<string>();
    for (const name of tableNames) {
      const prefix = Object.keys(TABLE_COLORS).find((p) => name.startsWith(p));
      if (prefix) set.add(prefix);
    }
    return [...set];
  }, [tableNames]);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        mx: -3,
        mt: -2,
      }}
    >
      {/* Header bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1,
          borderBottom: '1px solid #E4E4E7',
          backgroundColor: '#FAFAFA',
          flexShrink: 0,
        }}
      >
        <StorageIcon sx={{ color: '#054AD9', fontSize: 20 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Database ERD
        </Typography>
        <TextField
          size="small"
          placeholder="Filter tables..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 240, '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
        />
        <Typography variant="caption" sx={{ color: '#71717A' }}>
          {visibleTables.length} / {tableNames.length} tables
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
          {prefixes.map((p) => {
            const c = TABLE_COLORS[p];
            return (
              <Chip
                key={p}
                label={p.replace(/_$/, '')}
                size="small"
                sx={{
                  fontSize: 10,
                  height: 20,
                  backgroundColor: c.bg,
                  color: c.header,
                  border: `1px solid ${c.border}`,
                  cursor: 'pointer',
                }}
                onClick={() =>
                  setSearch((s) => (s === p.replace(/_$/, '') ? '' : p.replace(/_$/, '')))
                }
              />
            );
          })}
        </Box>
        <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
          Zoom: {Math.round(zoom * 100)}%
        </Typography>
      </Box>

      {/* Canvas */}
      <Box
        ref={containerRef}
        onMouseDown={handleBgMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        sx={{
          flex: 1,
          position: 'relative',
          overflow: 'hidden',
          cursor: panRef.current ? 'grabbing' : 'grab',
          backgroundColor: '#F8FAFC',
          backgroundImage: 'radial-gradient(#D1D5DB 1px, transparent 1px)',
          backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
          backgroundPosition: `${pan.x}px ${pan.y}px`,
        }}
      >
        <div
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          {/* FK relationship lines */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: 8000,
              height: 8000,
              pointerEvents: 'none',
            }}
          >
            {visibleFKs.map((fk) => {
              const srcPos = positions.get(fk.sourceTable);
              const tgtPos = positions.get(fk.targetTable);
              if (!srcPos || !tgtPos) return null;

              const srcY = getColumnY(fk.sourceTable, fk.sourceColumn);
              const tgtY = getColumnY(fk.targetTable, fk.targetColumn);

              const x1 = srcPos.x + 260;
              const y1 = srcPos.y + srcY;
              const x2 = tgtPos.x;
              const y2 = tgtPos.y + tgtY;

              const isHighlighted =
                selectedTable &&
                (fk.sourceTable === selectedTable || fk.targetTable === selectedTable);
              const midX = (x1 + x2) / 2;

              return (
                <g key={fk.constraintName}>
                  <path
                    d={`M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`}
                    fill="none"
                    stroke={isHighlighted ? '#054AD9' : '#CBD5E1'}
                    strokeWidth={isHighlighted ? 2 : 1}
                    strokeDasharray={isHighlighted ? undefined : '4 2'}
                    opacity={selectedTable && !isHighlighted ? 0.15 : 1}
                  />
                  {/* Arrow */}
                  <circle
                    cx={x2}
                    cy={y2}
                    r={3}
                    fill={isHighlighted ? '#054AD9' : '#94A3B8'}
                    opacity={selectedTable && !isHighlighted ? 0.15 : 1}
                  />
                </g>
              );
            })}
          </svg>

          {/* Table boxes */}
          {visibleTables.map((tableName) => {
            const pos = positions.get(tableName);
            if (!pos) return null;

            const cols = columnsByTable.get(tableName) ?? [];
            const color = getTableColor(tableName);
            const isSelected = selectedTable === tableName;
            const isConnected = connectedTables.has(tableName);
            const dimmed = selectedTable && !isConnected;

            return (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions
              <div
                key={tableName}
                onMouseDown={(e) => {
                  handleMouseDown(e, tableName);
                  setSelectedTable(tableName);
                }}
                style={{
                  position: 'absolute',
                  left: pos.x,
                  top: pos.y,
                  width: 260,
                  backgroundColor: color.bg,
                  border: `2px solid ${isSelected ? '#054AD9' : color.border}`,
                  borderRadius: 8,
                  boxShadow: isSelected
                    ? '0 4px 16px rgba(5, 74, 217, 0.2)'
                    : '0 1px 4px rgba(0,0,0,0.08)',
                  cursor: 'move',
                  userSelect: 'none',
                  opacity: dimmed ? 0.2 : 1,
                  transition:
                    'opacity 200ms cubic-bezier(0.2, 0.8, 0.4, 1), box-shadow 200ms cubic-bezier(0.2, 0.8, 0.4, 1)',
                  zIndex: isSelected ? 10 : 1,
                }}
              >
                {/* Table name header */}
                <div
                  style={{
                    padding: '6px 10px',
                    backgroundColor: color.header,
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: '6px 6px 0 0',
                    fontFamily: 'monospace',
                  }}
                >
                  {tableName}
                </div>

                {/* Columns */}
                <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {cols.map((col) => {
                    const isPk = pkColumns.has(`${tableName}.${col.name}`);
                    const isFk = fkColumns.has(`${tableName}.${col.name}`);

                    return (
                      <div
                        key={col.name}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '2px 10px',
                          fontSize: 11,
                          borderBottom: '1px solid rgba(0,0,0,0.04)',
                          gap: 4,
                        }}
                      >
                        {isPk && (
                          <span
                            style={{ fontSize: 9, fontWeight: 700, color: '#D97706', minWidth: 16 }}
                          >
                            PK
                          </span>
                        )}
                        {isFk && !isPk && (
                          <span
                            style={{ fontSize: 9, fontWeight: 700, color: '#054AD9', minWidth: 16 }}
                          >
                            FK
                          </span>
                        )}
                        {!isPk && !isFk && <span style={{ minWidth: 16 }} />}
                        <span
                          style={{
                            fontFamily: 'monospace',
                            fontWeight: isPk ? 700 : 400,
                            color: isFk ? '#054AD9' : '#1F2937',
                            flex: 1,
                          }}
                        >
                          {col.name}
                        </span>
                        <span
                          style={{
                            fontSize: 10,
                            color: '#9CA3AF',
                            fontFamily: 'monospace',
                          }}
                        >
                          {getTypeLabel(col)}
                        </span>
                        {!col.isNullable && (
                          <span style={{ fontSize: 8, color: '#EF4444', fontWeight: 700 }}>!</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </Box>
    </Box>
  );
};

export default DbErdComponent;
